/** @format */
import { Aside } from '@mpb-com/feather/components/atoms/aside';
import { useMeasure } from '@mpb-com/feather/hooks/use-measure';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';
import React, {
    FunctionComponent,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import Accordion from '@toucan/client/global/icons/legacy/accordion.svg';
import Arrow from '@toucan/client/global/icons/legacy/small-arrow.svg';

import {
    IconButton,
    IconButtonFill,
} from '@toucan/client/shared/components/icon-button';
import { Link, LinkColor } from '@toucan/client/shared/components/link';

import { CollapsibleAsideStyleProps, styles } from './collapsible-aside.styles';

export enum CollapsibleAsideVariant {
    STANDALONE = 'standalone',
    INLINE = 'inline',
}

export enum CollapsibleAsideTitlePosition {
    TOP = 'top',
    BOTTOM = 'bottom',
}

export enum CollapsibleAsideTitleColor {
    MIDNIGHT = 'midnight',
}

type DynamicTitle = {
    open: ReactNode;
    closed: ReactNode;
};

export type CollapsibleAsideProps = {
    /**
     * Child elements
     */
    children: ReactNode;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * A title to be displayed in the component
     */
    title: ReactNode | DynamicTitle;

    /**
     * Variant of the collapsible aside
     */
    variant?: CollapsibleAsideVariant;

    /**
     * Optional onClick event handler
     */
    onClick?: (isOpen: boolean) => void;

    /**
     * Optional disabled prop that defaults to false
     * @default false
     */
    disabled?: boolean;

    titlePosition?: CollapsibleAsideTitlePosition;

    $titleElementColor?: CollapsibleAsideTitleColor;
    defaultOpen?: boolean;
};

const isDynamicTitleType = (title: any): title is DynamicTitle =>
    Boolean(title.open);

const StyledCollapsibleAside = styled('div')<CollapsibleAsideStyleProps>`
    ${styles}
`;

export const CollapsibleAside: FunctionComponent<CollapsibleAsideProps> = ({
    title,
    children,
    variant = CollapsibleAsideVariant.STANDALONE,
    disabled = false,
    titlePosition = CollapsibleAsideTitlePosition.TOP,
    onClick,
    defaultOpen = false,
    ...otherProps
}: CollapsibleAsideProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

    const [contentHeight, setContentHeight] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const { height } = useMeasure(ref);
    const titleLocatedAtTop =
        titlePosition === CollapsibleAsideTitlePosition.TOP;
    const titleLocatedAtBottom =
        titlePosition === CollapsibleAsideTitlePosition.BOTTOM;

    const contentSpringStyle = useSpring({
        height: isOpen ? contentHeight : 0,
    });

    /**
     * Toggle whether the content should be display or not
     *
     * @returns {void}
     */
    function toggleOpen() {
        setIsOpen(!isOpen);
        onClick?.(!isOpen);
    }

    function renderTitle() {
        if (isDynamicTitleType(title)) {
            return isOpen ? title.open : title.closed;
        }
        return title;
    }

    useEffect(() => {
        setContentHeight(height);
        window.addEventListener('resize', () => setContentHeight(height));

        return window.removeEventListener('resize', () =>
            setContentHeight(height)
        );
    }, [height]);

    const titleElementVariations = {
        [CollapsibleAsideVariant.STANDALONE]: (
            <div
                className="title"
                data-testid="collapsible-aside-title"
                onClick={toggleOpen}
            >
                {renderTitle()}
                <IconButton
                    fill={isOpen ? IconButtonFill.SOLID : IconButtonFill.HOLLOW}
                    className="toggle-button"
                    label={t('Expand')}
                    data-testid="collapsible-aside-toggle-button"
                    border={true}
                >
                    <Arrow className="title-arrow" />
                </IconButton>
            </div>
        ),
        [CollapsibleAsideVariant.INLINE]: (
            <Link
                linkColor={LinkColor.DARK}
                className="title"
                data-testid="collapsible-aside-title"
                disabled={disabled}
                onClick={toggleOpen}
            >
                {renderTitle()}
                <div
                    className="toggle-button"
                    data-testid="collapsible-aside-toggle-button"
                >
                    <Accordion className="title-arrow" />
                </div>
            </Link>
        ),
    };

    return (
        <StyledCollapsibleAside
            isOpen={isOpen}
            variant={variant}
            $titleLocatedAtTop={titleLocatedAtTop}
            data-testid="collapsible-aside"
            {...otherProps}
        >
            <Aside border={variant !== CollapsibleAsideVariant.INLINE}>
                {titleLocatedAtTop && (
                    <div
                        className="collapsible-aside-title-container"
                        data-testid={'collapsible-aside-title__top'}
                    >
                        {titleElementVariations[variant]}
                    </div>
                )}

                <animated.div
                    style={contentSpringStyle}
                    className="content-container"
                    data-testid="collapsible-aside-content"
                    aria-hidden={!isOpen}
                >
                    <div className="content" ref={ref}>
                        {children}
                    </div>
                </animated.div>

                {titleLocatedAtBottom && (
                    <div
                        className="collapsible-aside-title-container"
                        data-testid={'collapsible-aside-title__bottom'}
                    >
                        {titleElementVariations[variant]}
                    </div>
                )}
            </Aside>
        </StyledCollapsibleAside>
    );
};
